import { useState } from 'react';

import { useFlag } from '@unleash/proxy-client-react';
import withAuth from 'hocs/withAuth/withAuth';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { authorDescription } from 'utils/helpers';
import { FeatureToggle } from 'utils/unleash/toggles';

import { useModals } from 'context';
import { useOnInit, useRequest } from 'hooks';
import { getSpecialistNotes } from 'services/specialist';
import {
  NotesProps,
  NotesRequestProps,
  SpecialistType,
} from 'services/specialist/types';
import {
  deleteEndUserNote,
  EndUserState,
  getEndUserInfo,
  postEndUserNote,
} from 'services/user';

import { HeaderUserInfo } from 'components/HeaderUserInfo/HeaderUserInfo';
import ItemList from 'components/ItemList';
import Loading from 'components/Loading';
import Paginator from 'components/Paginator';
import TabList from 'components/TabList';
import { FeedbackComponentProps, ConfirmComponentProps } from 'modals';
import { AddOrViewNoteComponentProps } from 'modals/AddOrViewNote';

import * as S from './styles';

const Notes: React.FC = ({ children }) => {
  const [userInfo, setUserInfo] = useState<EndUserState>();
  const [notes, setNotes] = useState<NotesRequestProps>();
  const [cursors] = useState<{ [key: string]: string }>({ 1: '' });
  const request = useRequest();
  const { openModal, closeModal } = useModals();
  const intl = useIntl();
  const navigate = useNavigate();

  const isPatientNotesEnabled = useFlag(FeatureToggle.PatientRecords);

  const { id = '' } = useParams<{
    id: string;
  }>();

  if (!isPatientNotesEnabled) {
    navigate(`/enduser/${id}`);
  }

  const getUserInfo = async () => {
    request.setLoading();
    try {
      const { data } = await getEndUserInfo(id);
      setUserInfo(data);
      request.setSuccess();
    } catch (e) {
      request.setError(e);
    }
  };

  const getNotes = async (currentPage: number) => {
    request.setLoading();
    try {
      const { data } = await getSpecialistNotes(id, cursors[currentPage]);
      setNotes(data);

      if (data.metadata.nextCursor !== '') {
        cursors[currentPage + 1] = data.metadata.nextCursor;
      }

      request.setSuccess();
    } catch (e) {
      request.setError(e);
    }
  };

  const updateNote = async (
    title: string,
    description: string,
    noteId: string,
  ) => {
    request.setLoading();
    try {
      await postEndUserNote(
        {
          cognito_user_id: id,
          title,
          note: description,
        },
        noteId,
      );
      request.setSuccess();
      openModal<FeedbackComponentProps>('feedback', {
        props: {
          title: 'NOTES.MODAL.EDIT.SUCCESS',
          status: 'success',
        },
        onClose: () => {
          closeModal('addOrViewNote');
          getNotes(1);
        },
      });
    } catch (e) {
      const fallbackMessage = 'Error posting end user note';
      request.setError(e, fallbackMessage);
      openModal<FeedbackComponentProps>('feedback', {
        props: {
          title: request.errorMessage() || fallbackMessage,
          status: 'error',
        },
      });
    }
  };

  const deleteNote = async (noteId: string) => {
    request.setLoading();
    try {
      await deleteEndUserNote(noteId);
      request.setSuccess();
      openModal<FeedbackComponentProps>('feedback', {
        props: {
          title: 'NOTES.MODAL.DELETE.SUCCESS',
          status: 'success',
        },
        onClose: () => {
          closeModal('addOrViewNote');
          getNotes(1);
        },
      });
    } catch (e) {
      const fallbackMessage = 'Error deleting end user note';

      request.setError(e, fallbackMessage);
      openModal<FeedbackComponentProps>('feedback', {
        props: {
          title: request.errorMessage() || fallbackMessage,
          status: 'error',
        },
      });
    }
  };

  const confirmDelete = (noteId: string) => {
    openModal<ConfirmComponentProps>('confirm', {
      props: {
        message: 'NOTES.MODAL.DELETE.MESSAGE',
        confirmLabel: 'NOTES.MODAL.DELETE.CONFIRM',
        onConfirm: () => deleteNote(noteId),
      },
    });
  };

  const openViewNoteModal = (noteState: NotesProps) => {
    try {
      openModal<AddOrViewNoteComponentProps>('addOrViewNote', {
        props: {
          isView: true,
          title: noteState.title,
          description: noteState.description,
          date: new Date(noteState.createdAt),
          noteId: noteState.id,
          author: authorDescription(
            noteState?.author?.name,
            noteState?.author?.specialistType === SpecialistType.CP
              ? intl.formatMessage({ id: 'PROFESSIONAL.CP' })
              : intl.formatMessage({ id: 'PROFESSIONAL.TC' }),
          ),
          onClickButton: (title, description) =>
            updateNote(title, description, noteState.id),
          onClickDelete: () => confirmDelete(noteState.id),
        },
      });
      request.setSuccess();
    } catch (e) {
      request.setError(e);
    }
  };

  const renderNotes = () => {
    // TODO: Remove this temporary interface when the backend returns the description attribute instead of the note
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const notesTempInterface = notes?.results?.map((note: any) => {
      return {
        ...note,
        description: note.note,
      };
    });

    return notesTempInterface?.map(note => {
      const createdAt = new Date(note.createdAt);

      return (
        <ItemList
          key={note.id}
          title={note.title}
          dateInfo={createdAt
            .toLocaleTimeString([], {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
            .replace(' ', '  ')}
          description={note.description}
          author={authorDescription(
            note?.author?.name,
            note?.author?.specialistType === SpecialistType.CP
              ? intl.formatMessage({ id: 'PROFESSIONAL.CP' })
              : intl.formatMessage({ id: 'PROFESSIONAL.TC' }),
          )}
          buttonTitle={intl.formatMessage({
            id: 'NOTE.VIEWDETAILS',
          })}
          onClick={() => openViewNoteModal(note as NotesProps)}
        />
      );
    });
  };

  useOnInit(() => {
    request.setLoading();
    getUserInfo();
    getNotes(1);
  });

  return (
    <S.UserInfoContainer>
      <Loading isLoading={request.isLoading()} />
      <HeaderUserInfo user={userInfo} />
      <S.Divisor />
      <S.TabsContainer data-testid="tabsContainer">
        <TabList selectedIndex={1} />
        <main>
          <S.CrumbContainer data-testid="notes-breadcrumb">
            {children}
          </S.CrumbContainer>
          <S.Divisor />
          <S.NoteList data-testid="notes">
            {renderNotes()}
            <S.NoteFooter>
              <Paginator
                next={(currentPage: number) => getNotes(currentPage + 1)}
                previous={(currentPage: number) => getNotes(currentPage - 1)}
                lastPage={notes?.metadata.totalPages}
              />
            </S.NoteFooter>
          </S.NoteList>
        </main>
      </S.TabsContainer>
    </S.UserInfoContainer>
  );
};

export default withAuth(Notes);
